import React, { useRef, useState, useEffect } from "react";
import { type GetStaticProps, type NextPage } from "next";
import { getAnnouncementBanner, type ComposableCdpProps, type Banner, getComposableCdpHub } from "lib/sanity";
import { Page } from "components/page";
import { HalfHero } from "components/half-hero";
import { RadialGradient } from "ui/radial-gradient";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useHeaderHeight } from "contexts/header-height";
import { ArticleCard, YoutubeCard, BlogCard, CertifiedCard, IntroText } from "components/platform/composable-cdp";
import { Section } from "components/section";
import { DemoForm } from "components/demo-form";
import { ArrowLink } from "ui/link";
import { ResourcesSlider } from "ui/slider";
import { horizontalContainerPadding } from "ui/container";
import { getPageMetaImage } from "utils";
import { Button } from "ui/button";
import Balancer from "react-wrap-balancer";
import heroImage from "../../../public/assets/platform/composable-cdp/hero.png";

type PageSlider = {
  id: string;
  title: string;
  description?: string;
  slides: JSX.Element[];
};

function getSliders(page: ComposableCdpProps): PageSlider[] {
  const sliders = [
    {
      id: "industry-leaders",
      title: "Industry leaders",
      slides: page.externalArticles.map((article) => <ArticleCard key={article.title} {...article} />),
    },
    {
      id: "practitioners",
      title: "Practitioners",
      slides: page.youtubeVideos.map((video) => <YoutubeCard key={video.title} {...video} />),
    },
    {
      id: "certified-sources",
      title: "Certified data warehouses",
      description:
        "Recognized providers offering robust data storage solutions, enabling the rapid and reliable deployment of Composable CDPs.",
      slides: page.sources.map((source) => (
        <CertifiedCard
          key={source.slug}
          hasName
          url={source.vendorUrl ?? `/integrations/sources/${source.slug}`}
          name={source.name}
          imageUrl={source.iconUrl}
        />
      )),
    },
    {
      id: "certified-destinations",
      title: "Certified destinations",
      description:
        "Platforms dedicated to offering integrations compatible with Composable CDPs, ensuring seamless data transfers with data warehouses.",
      slides: page.destinations.map((destination) => (
        <CertifiedCard
          key={destination.slug}
          hasName
          url={destination.vendorUrl ?? `/integrations/destinations/${destination.slug}`}
          name={destination.name}
          imageUrl={destination.iconUrl}
        />
      )),
    },
    {
      id: "certified-analytics",
      title: "Certified analytics tools",
      description:
        "Software companies specializing in analytics, attribution, and measurement that prioritize integrations with Composable CDPs to enhance data analysis capabilities.",
      slides: page.analyticsTools.map((tool) => <CertifiedCard key={tool.url} {...tool} />),
    },
    {
      id: "composable-certified-service-partners",
      title: "Certified service partners",
      description:
        "Partners with a proven track record in successful Composable CDP implementations, demonstrating expertise and effectiveness.",
      slides: page.servicePartners.map((partner) => <CertifiedCard key={partner.url} {...partner} />),
    },
    {
      id: "hightouch-blog",
      title: "Hightouch on Composable CDPs",
      slides: page.blogs.map((blog) => <BlogCard key={blog.slug} {...blog} />),
    },
  ];

  return sliders;
}

type PageProps = {
  page: ComposableCdpProps;
  announcementBanner?: Banner;
};

/**
 * Used to calculate the amount of space in the DOM needed to horizontally align the sidebar
 * and slider navigation elements with the edges of the info section container.
 */
function getSectionAlignmentValue(refWidth?: number) {
  const sectionWidth = refWidth ? refWidth.toString() + "px" : "var(--chakra-sizes-container-section)";
  return `calc((100vw - ${sectionWidth}) / 2)`;
}

const ComposableCdp: NextPage<Readonly<PageProps>> = ({ page, announcementBanner }) => {
  const { headerHeight } = useHeaderHeight();
  const sectionContainer = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1200); // Default to `container.section`
  const pageSlides = getSliders(page);

  useEffect(() => {
    const gutterObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    if (sectionContainer.current) {
      gutterObserver.observe(sectionContainer.current);
    }

    return () => {
      gutterObserver.disconnect();
    };
  }, [containerWidth]);

  return (
    <Page
      hasSmoothScroll
      title={page.metaTitle}
      description={page.metaDescription ?? page.description}
      image={getPageMetaImage({ src: page.metaImage, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      footerOptions={{
        ctaProps: {
          title: <Balancer>Join the community on LinkedIn</Balancer>,
          description: "",
          actions: (
            <Button hasRightChevron href="https://www.linkedin.com/groups/14348481/" target="_blank">
              Join the group
            </Button>
          ),
        },
      }}
    >
      <HalfHero
        hideCTA
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        containerProps={{ gap: { base: 0, lg: 12 } }}
        image={heroImage}
        imageObjectFit="contain"
        imageFlex={0.9}
        pb={{ base: 4, md: 0 }}
        backgroundGradient={
          <>
            <RadialGradient variant="avocado" top="-10%" left="-20%" width="60%" height="30%" opacity={0.6} />
            <RadialGradient
              variant="cyan"
              top={{ base: "-40%", md: "-32%" }}
              right={{ base: "-12%", md: "-36%" }}
              boxSize={{ base: "70%", md: "90%" }}
              opacity={0.6}
            />
            <RadialGradient
              variant="mint"
              top={{ base: "-40%", md: "-30%" }}
              right={{ base: "-40%", md: "-60%" }}
              boxSize={{ base: "70%", md: "90%" }}
            />
            <RadialGradient variant="lightspeed" top="-50%" right="-40%" boxSize="50%" opacity={0.6} />
          </>
        }
      />

      <Box ref={sectionContainer} as="section" maxW="container.section" mx="auto" px={horizontalContainerPadding}>
        <IntroText />
      </Box>

      <Section fullWidth my={0} pb="huge">
        <Flex pos="relative">
          <Flex
            as="aside"
            align="start"
            display={{ base: "none", lg: "flex" }}
            pos="sticky"
            top={`${headerHeight + 20}px`}
            direction="column"
            gap={8}
            flexShrink={0}
            width="300px"
            // MaxHeight = headerHeight + 20px top padding + 20px bottom padding for breathing room
            maxHeight={`calc(100vh - ${headerHeight + 40}px)`}
            // Add right padding for scroll bar
            pr={5}
            mt={32}
            mr={{ base: 12, xl: 20 }}
            // Set marginLeft to standard section left margin
            ml={getSectionAlignmentValue(sectionContainer.current?.offsetWidth)}
            overflowY="auto"
          >
            <Flex as="nav" aria-label="Table of contents" maxHeight="100%" direction="column" gap={4} flexShrink={0}>
              <Text fontSize="lg" fontWeight="semibold">
                Composable CDP resources
              </Text>

              <Flex direction="column">
                {pageSlides.map(({ id, title }) => (
                  <Text
                    key={title}
                    as="a"
                    py={1}
                    href={`#${id}`}
                    opacity={0.7}
                    _hover={{ opacity: 1 }}
                    fontSize="sm"
                    transition="150ms opacity"
                  >
                    {title}
                  </Text>
                ))}
              </Flex>
            </Flex>

            <ArrowLink size="sm" href="https://www.linkedin.com/groups/14348481/" target="_blank">
              Join the community on LinkedIn
            </ArrowLink>

            <Box w="100%">
              <Text mb={4} fontSize="lg" fontWeight="semibold">
                Book a demo
              </Text>

              <DemoForm />
            </Box>
          </Flex>

          <Box
            flex={1}
            overflow="hidden"
            maxW={{ base: "100vw", lg: "100%" }}
            mr={{ base: -4, xl: 0 }}
            pt={{ base: 20, md: 32 }}
          >
            {pageSlides.map(({ id, title, description, slides }, index) => (
              <Box
                key={id}
                id={id}
                w="100%"
                maxW={{ base: "100vw", lg: "100%" }}
                scrollMarginTop={32}
                mb={index === pageSlides.length - 1 ? { base: 0, lg: 16 } : { base: 20, lg: 32 }}
              >
                <ResourcesSlider
                  id={`${id}-slider`}
                  title={title}
                  description={description}
                  slides={slides}
                  slidesOffsetAfter={32}
                  spaceBetween={20}
                  navigationPr={{
                    base: 4,
                    xl: getSectionAlignmentValue(sectionContainer.current?.offsetWidth),
                  }}
                  breakpoints={{
                    300: {
                      slidesOffsetBefore: 4,
                    },
                    800: {
                      slidesOffsetBefore: 0,
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Flex>
      </Section>
    </Page>
  );
};

export default ComposableCdp;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner] = await Promise.all([getComposableCdpHub(), getAnnouncementBanner()]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
    },
    revalidate: 60,
  };
};
